import { useInfiniteQuery } from "@tanstack/react-query";

import { PAGE_LIMIT, apiGetPage, nextCursor, qk } from "@/shared/api";

import type { CommentItem, CommentSort } from "./types";

/**
 * Лента отзывов робота (доработки §6.1).
 *
 * `placeholderData` держит предыдущую вкладку на экране, пока грузится новая:
 * тот же приём, что на экране рейтинга. Без него каждое переключение
 * «Полезные / Новые / С оценкой» схлопывало бы список в скелет, хотя данные
 * почти те же.
 *
 * Курсоры сортировок несовместимы по устройству (у «Полезных» ключ — число
 * голосов, у «Новых» — дата), поэтому сортировка входит в ключ кэша: при смене
 * вкладки пагинация начинается заново, а не продолжается чужим курсором.
 */
export function useRobotComments(robotId: number, sort: CommentSort, enabled = true) {
  return useInfiniteQuery({
    queryKey: qk.robotComments(robotId, sort),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGetPage("/api/v1/robots/{robot_id}/comments", {
        params: { robot_id: robotId },
        query: { sort, limit: PAGE_LIMIT, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      }),
    getNextPageParam: (last) => nextCursor(last.meta),
    placeholderData: (previous) => previous,
  });
}

/**
 * Плоский список без дублей.
 *
 * Дедупликация обязательна, а не гигиена: «Полезные» и «С оценкой» листаются
 * по ИЗМЕНЧИВОМУ ключу, и отзыв, набравший голос между двумя страницами, может
 * приехать дважды. Одинаковые React-ключи в этом случае — не косметика, а
 * рассинхрон состояния соседних карточек.
 */
export function flattenComments(
  pages: { items: CommentItem[] }[] | undefined,
): CommentItem[] {
  const seen = new Set<number>();
  const rows: CommentItem[] = [];
  for (const page of pages ?? []) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      rows.push(item);
    }
  }
  return rows;
}
